import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { QuizScore, QuizResultType } from '../types';
import { useLanguage } from '../LanguageContext';
import { CONTENT } from '../content';

export default function VBCRealityCheck() {
  const { language } = useLanguage();
  const t = CONTENT[language].quiz;
  const ui = t.ui;
  const questionsData = t.questions;

  const [step, setStep] = useState<'intro' | 'question' | 'result'>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState<QuizScore>({ riskTolerance: 0, adminBurden: 0, infrastructure: 0 });
  const [resultType, setResultType] = useState<QuizResultType | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to results when available
  useEffect(() => {
    if (step === 'result' && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [step]);

  const handleAnswer = (optionScore: Partial<QuizScore>) => {
    const newScore = {
      riskTolerance: score.riskTolerance + (optionScore.riskTolerance || 0),
      adminBurden: score.adminBurden + (optionScore.adminBurden || 0),
      infrastructure: score.infrastructure + (optionScore.infrastructure || 0),
    };
    setScore(newScore);

    if (currentQuestionIndex < questionsData.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      calculateResult(newScore);
    }
  };

  const calculateResult = (finalScore: QuizScore) => {
    if (finalScore.riskTolerance < 5 && finalScore.adminBurden < 10) {
      setResultType(QuizResultType.FFS_WARRIOR);
    } else if (finalScore.riskTolerance < 12) {
      setResultType(QuizResultType.UPSIDE_SAVER);
    } else if (finalScore.riskTolerance >= 12 && finalScore.infrastructure >= 5) {
      setResultType(QuizResultType.RISK_READY);
    } else {
      setResultType(QuizResultType.BLACK_BOX_VICTIM);
    }
    setStep('result');
  };

  const currentQuestion = questionsData[currentQuestionIndex];
  const resultData = resultType ? t.results[resultType] : null;

  return (
    <div className="bg-suncoast-bg min-h-[80vh] py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        
        {step === 'intro' && (
          <div className="bg-white p-12 rounded-2xl shadow-xl text-center animate-fade-in">
            <h1 className="text-3xl font-bold text-suncoast-dark mb-4">{ui.title}</h1>
            <p className="text-gray-600 text-lg mb-8">{ui.intro}</p>
            <div className="flex justify-center">
              <button 
                onClick={() => setStep('question')}
                className="px-8 py-4 bg-suncoast-primary text-white font-bold rounded-lg hover:bg-teal-800 transition-colors shadow-lg"
              >
                {ui.startBtn}
              </button>
            </div>
            <div className="mt-8 text-sm text-gray-400">
              {ui.meta}
            </div>
          </div>
        )}

        {step === 'question' && (
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl transition-all">
            <div className="mb-8 flex justify-between items-center">
              <span className="text-xs font-bold text-suncoast-secondary uppercase tracking-widest">{ui.questionCount} {currentQuestionIndex + 1} / {questionsData.length}</span>
              <div className="w-32 h-2 bg-gray-100 rounded-full">
                <div 
                  className="h-full bg-suncoast-accent rounded-full transition-all duration-300" 
                  style={{ width: `${((currentQuestionIndex + 1) / questionsData.length) * 100}%` }}
                ></div>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-8">{currentQuestion.question}</h2>
            
            <div className="space-y-4">
              {currentQuestion.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(opt.score)}
                  className="w-full text-left p-5 border-2 border-gray-100 rounded-xl hover:border-suncoast-primary hover:bg-suncoast-bg/20 transition-all font-medium text-gray-700 flex items-center justify-between group"
                >
                  <span>{opt.label}</span>
                  <span className="opacity-0 group-hover:opacity-100 text-suncoast-primary transition-opacity">→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'result' && resultData && (
          <div className="animate-fade-in" ref={resultsRef}>
             <div className="bg-white p-8 md:p-12 rounded-t-2xl shadow-xl border-b border-gray-100">
                <div className="inline-block px-3 py-1 bg-suncoast-accent text-white text-xs font-bold rounded mb-4 tracking-wide">{ui.yourArchetype}</div>
                <h2 className="text-4xl font-bold text-suncoast-dark mb-4">{resultData.title}</h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">{resultData.description}</p>
                
                <div className="grid md:grid-cols-2 gap-8 mb-10">
                  <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                    <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      {ui.painPoints}
                    </h3>
                    <ul className="space-y-3">
                      {resultData.painPoints.map((point, i) => (
                        <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                          <span className="mt-1">•</span> {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 p-6 rounded-xl border border-green-100">
                    <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                       <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                       {ui.fix}
                    </h3>
                    <p className="text-sm text-green-700 leading-relaxed">
                      {resultData.suncoastFix}
                    </p>
                  </div>
                </div>

                <div className="text-center p-6 bg-suncoast-offWhite rounded-xl">
                  <h3 className="text-lg font-bold text-suncoast-dark mb-2">{ui.bookCta}</h3>
                  <p className="text-gray-600 mb-6">{ui.bookDesc}</p>
                  <Link 
                    to="/contact" 
                    className="inline-block px-8 py-4 bg-suncoast-primary text-white font-bold rounded-lg shadow-lg hover:bg-teal-800 transform hover:-translate-y-1 transition-all"
                  >
                    {CONTENT[language].common.bookPerformanceCheck}
                  </Link>
                </div>
             </div>

             {/* Capitation Truth Table Mini */}
             <div className="bg-white p-8 md:p-12 rounded-b-2xl shadow-xl border-t border-gray-200">
               <div className="flex flex-col md:flex-row justify-between items-end mb-6">
                 <div>
                    <h3 className="font-bold text-gray-800 text-lg">{ui.truthTableTitle}</h3>
                    <p className="text-sm text-gray-500">{ui.truthTableDesc}</p>
                 </div>
               </div>
               
               <div className="overflow-x-auto">
                 <table className="w-full text-sm text-left">
                   <thead>
                     <tr className="bg-gray-50 border-b border-gray-200">
                       {ui.tableHeaders.map((header, i) => (
                         <th key={i} className="p-4 font-bold text-gray-600">{header}</th>
                       ))}
                     </tr>
                   </thead>
                   <tbody>
                     <tr className="border-b border-gray-100 hover:bg-gray-50">
                       <td className="p-4 font-medium text-gray-800">{ui.tableRows.ffs[0]}</td>
                       <td className="p-4 text-gray-500">{ui.tableRows.ffs[1]}</td>
                       <td className="p-4 text-gray-500">{ui.tableRows.ffs[2]}</td>
                       <td className="p-4 text-red-500 font-medium">{ui.tableRows.ffs[3]}</td>
                     </tr>
                     <tr className="border-b border-gray-100 hover:bg-gray-50">
                       <td className="p-4 font-medium text-gray-800">{ui.tableRows.shared[0]}</td>
                       <td className="p-4 text-gray-500">{ui.tableRows.shared[1]}</td>
                       <td className="p-4 text-yellow-600 font-medium">{ui.tableRows.shared[2]}</td>
                       <td className="p-4 text-yellow-600 font-medium">{ui.tableRows.shared[3]}</td>
                     </tr>
                     <tr className="bg-suncoast-bg/20 border-l-4 border-suncoast-primary">
                       <td className="p-4 font-bold text-suncoast-primary">{ui.tableRows.suncoast[0]}</td>
                       <td className="p-4 text-suncoast-dark">{ui.tableRows.suncoast[1]}</td>
                       <td className="p-4 text-green-700 font-bold">{ui.tableRows.suncoast[2]}</td>
                       <td className="p-4 text-green-700 font-bold">{ui.tableRows.suncoast[3]}</td>
                     </tr>
                   </tbody>
                 </table>
               </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
}
